import React, { HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'primary' | 'secondary' | 'white' | 'current';
    label?: string;
    fullScreen?: boolean;
}

const Spinner = ({
    size = 'md',
    variant = 'primary',
    label,
    fullScreen = false,
    className,
    ...props
}: SpinnerProps) => {
    const sizeStyles = {
        xs: 'w-4 h-4 border-2',
        sm: 'w-6 h-6 border-2',
        md: 'w-8 h-8 border-3',
        lg: 'w-12 h-12 border-3',
        xl: 'w-16 h-16 border-4',
    };

    const variantStyles = {
        primary: 'border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400',
        secondary: 'border-gray-200 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400',
        white: 'border-white/30 border-t-white',
        current: 'border-current/30 border-t-current',
    };

    const spinner = (
        <div
            className={cn(
                'inline-block rounded-full animate-spin',
                sizeStyles[size],
                variantStyles[variant],
                className
            )}
            role="status"
            aria-label={label || 'Loading'}
            {...props}
        >
            <span className="sr-only">{label || 'Loading...'}</span>
        </div>
    );

    if (fullScreen) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                    {spinner}
                    {label && (
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                    )}
                </div>
            </div>
        );
    }

    if (label) {
        return (
            <div className="flex items-center gap-3">
                {spinner}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            </div>
        );
    }

    return spinner;
};

Spinner.displayName = 'Spinner';

// Predefined spinner patterns
export const SpinnerOverlay = ({
    show,
    label,
    size = 'lg',
}: {
    show: boolean;
    label?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}) => {
    if (!show) return null;

    return (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg">
            <Spinner size={size} label={label} />
        </div>
    );
};

export const SpinnerButton = ({
    size = 'sm',
    className,
}: {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}) => {
    return <Spinner size={size} variant="current" className={className} />;
};

export const SpinnerPage = ({ label }: { label?: string }) => {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
            <Spinner size="lg" label={label} />
        </div>
    );
};

export default Spinner;
